import React, { useEffect, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  useAsyncValue,
  useNavigate,
  useSubmit,
  useActionData,
} from 'react-router-dom';

// Components
import MaterialsForm from '../../../../../components/forms/ActionForms/MaterialsForm';

// Types
import { MaterialFormValues } from '../../../../../components/forms/ActionForms/MaterialsForm/types';
import { OrderType } from '../../../../../types/orders';
import { TabValidationStatus } from '../../../../../utils/formValidation';

// Redux
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { openModal } from '../../../../../state/actions/ui/modal';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';
import { modalIds } from '../../../../../components/modals/SuperModal/types';

interface ActionValidationResult {
  validationFailed?: boolean;
  tabsWithErrors?: TabValidationStatus[];
  validationSummary?: string;
}

/**
 * Rutas de cada pestaña cuando el paso terminal es MATERIALES (actuación
 * básica). La entrada que apunta a `'.'` es siempre la del paso terminal.
 */
const TAB_ROUTES: Record<string, string> = {
  'operators-and-vehicles': '../operators-and-vehicles',
  'location': '../location',
  'edition': '../edition',
  'budget-items': '../budget-items',
  'materials': '.',
  'machinery': '../machinery',
  'animals': '../animals',
};

export default function MaterialsFormPage(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const submit = useSubmit();
  const loaderData = useAsyncValue() as MaterialFormValues;
  const actionData = useActionData() as ActionValidationResult | undefined;

  // Actuació bàsica: Materiales es el ÚLTIMO paso del asistente desde
  // 2026-08-11 (la pestaña Animales se retiró: ni ella ni sus datos se usan en
  // el recorrido). Igual que Maquinaria, se detecta por la ruta: una básica
  // solo se CREA dentro de `/surveillance-route/`.
  //
  // Spec: docs/superpowers/specs/2026-08-11-basica-sin-fauna-design.md
  const isSRContext = window.location.pathname.startsWith(
    '/surveillance-route/'
  );

  useLayoutEffect(() => {
    dispatch(
      roidOTSelected(
        OrderType.Action,
        loaderData.breadCrumbsData.orderParentRoid,
        loaderData.breadCrumbsData.orderParentId
      )
    );
  }, []);

  // Modal de errores de validación — solo puede llegar aquí siendo terminal.
  useEffect(() => {
    if (!actionData?.validationFailed || !actionData?.tabsWithErrors) return;

    dispatch(
      openModal(modalIds.VALIDATION_ERRORS_MODAL, {
        tabsWithErrors: actionData.tabsWithErrors,
        validationSummary: actionData.validationSummary,
        onNavigateToTab: (tabKey: string) => {
          navigate(TAB_ROUTES[tabKey] || `../${tabKey}`, {
            state: { validateOnMount: true },
          });
        },
      })
    );
  }, [actionData, dispatch, navigate]);

  const onFormSubmit = () => {
    if (!isSRContext) {
      // Actuación normal: el asistente sigue hasta Maquinaria y termina en
      // Animales, que es quien guarda.
      navigate('../machinery');
      return;
    }

    // Básica: aquí se acaba el asistente. El `action` de la ruta corre la
    // validación centralizada y el guardado completo — el mismo que dispara
    // Animales en la actuación normal.
    submit(new FormData(), { method: 'POST', action: '.' });
  };

  return (
    <MaterialsForm
      onFormSubmit={onFormSubmit}
      initialData={loaderData}
      sessionStorageKey={formActionNaming.NEW_MATERIALS_FORM}
      isTerminalStep={isSRContext}
    />
  );
}
